import { PrivateAPINamespace } from '../PrivateAPINamespace';
import type { IRouter } from '@wix/public-editor-platform-interfaces';
import { RouterHandle } from './RouterHandle';
import { addRouterExtension } from './addRouterExtension';

export class RoutersNamespace extends PrivateAPINamespace {
  static readonly namespace = 'routers';

  async isValidPrefix(prefix: string): Promise<boolean> {
    if (!this.appDefinitionId) {
      throw new Error('appDefinitionId is not bound');
    }
    const result = this.ps.ds.routers.isValidPrefixByAppDefId(
      this.appDefinitionId,
      prefix,
    );
    return result.valid;
  }

  async list(): Promise<IRouter[]> {
    if (!this.appDefinitionId) {
      throw new Error('appDefinitionId is not bound');
    }
    return this.ps.ds.routers.get
      .byApp(this.appDefinitionId)
      .map(({ prefix }) => {
        const routerRef = this.ps.ds.routers.getRouterRef.byPrefix(prefix);
        if (!routerRef) {
          return null;
        }
        const editorPointer =
          this.ps.platformPackagesBridge.EditorPointerAPI?.buildEditorPointer(
            routerRef,
            routerRef.innerPath,
          );
        if (!editorPointer) {
          return null;
        }
        return new RouterHandle(this.ps, editorPointer).asIRouter();
      })
      .filter(Boolean) as IRouter[];
  }

  async add(extensionId: string): Promise<IRouter> {
    if (!this.appDefinitionId) {
      throw new Error('appDefinitionId is not bound');
    }
    const appData = this.ps.ds.platform.getAppDataByAppDefId(
      this.appDefinitionId,
    );
    const component = appData?.components?.find(
      (c) => c.componentId === extensionId && c.type === 'APP_ROUTER',
    );

    if (!component) {
      throw new Error(
        `Router extension not found: no APP_ROUTER component with id "${extensionId}" for app "${this.appDefinitionId}"`,
      );
    }

    const preferredPrefix = (component.data as { preferredPrefix?: string })
      ?.preferredPrefix;
    if (!preferredPrefix) {
      throw new Error(
        `Router extension "${extensionId}" has no preferredPrefix defined`,
      );
    }

    const routerRef = await addRouterExtension(this.ps.ds, {
      appDefinitionId: this.appDefinitionId,
      extensionId,
      preferredPrefix,
      // TODO: [EP-8488] decide if pages should be connected when adding a router via SDK
      pagesToConnect: [],
    });

    const editorPointerAPI = this.ps.platformPackagesBridge.EditorPointerAPI;
    if (!editorPointerAPI) {
      throw new Error('EditorPointerAPI is not available');
    }

    const editorPointer = editorPointerAPI.buildEditorPointer(
      routerRef,
      routerRef.innerPath,
    );

    return new RouterHandle(this.ps, editorPointer).asIRouter();
  }
}
